import { CommonModule } from '@angular/common';
import { Component, OnDestroy, ViewEncapsulation, NgZone } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BaseService } from 'src/app/shared/services/base.service';
import { GlobalService } from 'src/app/shared/services/global.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-smiley',
  templateUrl: './smiley.component.html',
  styleUrls: ['./smiley.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, ReactiveFormsModule, MatTooltipModule, TranslateModule],
  standalone: true,
})
export class SmileyComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  // Drag state
  private isDragging = false;
  private wasDragged = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private offsetX = 0; // offset entre o ponteiro e o left do elemento
  private offsetY = 0; // offset entre o ponteiro e o top do elemento
  private rafId: number | null = null;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private dragEl: HTMLElement | null = null;
  private DRAG_THRESHOLD = 5;

  private boundMove = (e: PointerEvent) => this.onPointerMove(e);
  private boundUp = (e: PointerEvent) => this.onPointerUp(e);

  public isDialogOpen = false;

  constructor(
    public dialog: MatDialog,
    public baseService: BaseService,
    public globalService: GlobalService,
    private ngZone: NgZone,
  ) {}

  /**
   * Início do drag — calcula o offset inicial uma única vez
   * e regista os listeners fora do Angular para máxima performance.
   */
  onDragStart(event: PointerEvent) {
    event.preventDefault(); // Impede o drag nativo da imagem pelo browser

    const el = event.target as HTMLElement;
    const rect = el.getBoundingClientRect();

    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.offsetX = event.clientX - rect.left;
    this.offsetY = event.clientY - rect.top;
    this.isDragging = false;
    this.wasDragged = false;
    this.dragEl = el;

    el.setPointerCapture(event.pointerId);

    this.ngZone.runOutsideAngular(() => {
      el.addEventListener('pointermove', this.boundMove);
      el.addEventListener('pointerup', this.boundUp);
    });
  }

  private onPointerMove(e: PointerEvent) {
    const dx = e.clientX - this.dragStartX;
    const dy = e.clientY - this.dragStartY;

    // Só começa a arrastar após ultrapassar o threshold
    if (!this.isDragging) {
      if (Math.abs(dx) > this.DRAG_THRESHOLD || Math.abs(dy) > this.DRAG_THRESHOLD) {
        this.isDragging = true;
        this.wasDragged = true;
        this.dragEl?.classList.add('smiley-dragging');
      } else {
        return;
      }
    }

    this.lastPointerX = e.clientX;
    this.lastPointerY = e.clientY;

    // requestAnimationFrame para sincronizar com o refresh do ecrã
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => this.updatePosition());
    }
  }

  private updatePosition() {
    this.rafId = null;
    if (!this.dragEl) return;

    const el = this.dragEl;
    const elW = el.offsetWidth;
    const elH = el.offsetHeight;
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;

    // Calcula left/top com base no ponteiro menos o offset inicial
    let newLeft = this.lastPointerX - this.offsetX;
    let newTop = this.lastPointerY - this.offsetY;

    // Limitar dentro da viewport
    newLeft = Math.max(0, Math.min(newLeft, vpW - elW));
    newTop = Math.max(0, Math.min(newTop, vpH - elH));

    // Converter para right/bottom (o CSS usa right/bottom por defeito)
    el.style.right = vpW - newLeft - elW + 'px';
    el.style.bottom = vpH - newTop - elH + 'px';
    // Limpar left/top caso existam
    el.style.left = 'auto';
    el.style.top = 'auto';
  }

  private onPointerUp(e: PointerEvent) {
    const el = this.dragEl;
    if (el) {
      el.releasePointerCapture(e.pointerId);
      el.classList.remove('smiley-dragging');
      el.removeEventListener('pointermove', this.boundMove);
      el.removeEventListener('pointerup', this.boundUp);
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.isDragging = false;
    this.dragEl = null;
  }

  async SmileyHelp() {
    // Se acabou de arrastar, não abre o diálogo
    if (this.wasDragged) {
      this.wasDragged = false;
      return false;
    }

    if (this.dialog.openDialogs && this.dialog.openDialogs.length > 0) {
      console.log('Já está aberto');
      return false;
    }

    window.history.pushState(null, null, window.location.href);
    const { SmileyHelpComponent } = await import('../smiley-help.component');
    let dialogRef: any;
    dialogRef = this.dialog.open(SmileyHelpComponent, {
      panelClass: 'app-full-bleed-dialog',
      position: {
        bottom: '45px',
        right: '25px',
      },
    });

    dialogRef.autoFocus = true;
    dialogRef.disableClose = true;
    this.isDialogOpen = true;
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isDialogOpen = false;
      });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }
    if (!this.destroy$.closed) {
      this.destroy$.next();
      this.destroy$.complete();
    }
  }
}
